import {
  LayoutDashboard,
  ClipboardList,
  ChefHat,
  ScrollText,
  Banknote,
  Coffee,
  LayoutGrid,
  Store,
  Boxes,
  Receipt,
  Bookmark,
  Wallet,
  Crown,
  BarChart3,
  Users,
  History,
  Settings as SettingsIcon,
  GraduationCap,
  ShieldCheck,
  Gamepad2,
  type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@cafe-mgmt/rbac';

import { can, canAny, hasFeature, type Me } from '@/lib/api';

// =========================================================================
// Single source of truth for admin navigation.
//
// Both the sidebar (AdminShell) and the Site map page render from this list,
// so the two can never drift. Each item declares the permission(s) that gate
// it; `visibleSections` filters the tree down to what a given member may see
// and drops any group left empty. Descriptions are written for the Site map —
// one short line explaining what each section is for.
// =========================================================================

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  description: string;
  /** Exact-match route (used by the Dashboard "/admin" index). */
  end?: boolean;
  /** Single permission gate. */
  perm?: Permission;
  /** Visible if the member holds ANY of these (management pages). */
  anyOf?: Permission[];
  /** Plan-feature gate (key from billing.Registry). Hidden unless the tenant's
   *  plan includes it — stacks with any perm/anyOf gate. */
  feature?: string;
  /** Sidebar-only render hook (e.g. the Shift open/closed pill). */
  badge?: 'shift';
};

export type NavGroup = { title: string; items: NavItem[] };

export const NAV_SECTIONS: NavGroup[] = [
  {
    title: 'Operations',
    items: [
      {
        to: '/admin',
        label: 'Dashboard',
        icon: LayoutDashboard,
        description: 'At-a-glance sales, cafe balance and alerts.',
        end: true,
        perm: 'report:read',
      },
      {
        to: '/admin/floor',
        label: 'Floor',
        icon: ClipboardList,
        description: 'Open tabs and the live service floor.',
        perm: 'order:read',
      },
      {
        to: '/admin/kitchen',
        label: 'Kitchen',
        icon: ChefHat,
        description: 'Kitchen display of pending tickets.',
        perm: 'kitchen:read',
      },
      {
        to: '/admin/history',
        label: 'History',
        icon: ScrollText,
        description: 'Closed serves day-by-day, with each day’s sales total and cash/online split.',
        perm: 'order:read',
      },
      {
        to: '/admin/shift',
        label: 'Shift',
        icon: Banknote,
        description: 'Open or close the till and reconcile the cash drawer.',
        perm: 'shift:read',
        badge: 'shift',
      },
    ],
  },
  {
    title: 'Catalog',
    items: [
      {
        to: '/admin/menu',
        label: 'Menu',
        icon: Coffee,
        description: 'Items, categories, pricing and cost.',
        anyOf: ['menu:create', 'menu:update', 'menu:delete'],
      },
      {
        to: '/admin/tables',
        label: 'Tables',
        icon: LayoutGrid,
        description: 'Service tables and floor layout.',
        anyOf: ['table:create', 'table:update', 'table:delete'],
      },
      {
        to: '/admin/outlets',
        label: 'Stations',
        icon: Store,
        description: 'Prep stations (Kitchen, Bar) and their printers.',
        anyOf: ['outlet:create', 'outlet:update', 'outlet:delete'],
        feature: 'multi_outlet',
      },
    ],
  },
  {
    title: 'Grow',
    items: [
      {
        to: '/admin/engage',
        label: 'Engage',
        icon: Gamepad2,
        description:
          'A café-wide QR guests scan to play for a reward — campaign, rewards, results and opt-in contacts.',
        anyOf: ['engage:read', 'engage:update'],
        feature: 'qr_rewards',
      },
    ],
  },
  {
    title: 'Admin',
    items: [
      {
        to: '/admin/inventory',
        label: 'Inventory',
        icon: Boxes,
        description: 'Stock levels and low-stock alerts.',
        perm: 'inventory:read',
        feature: 'inventory',
      },
      {
        to: '/admin/expenses',
        label: 'Expenses',
        icon: Receipt,
        description: 'Record and review spending.',
        perm: 'expense:read',
      },
      {
        to: '/admin/house-tabs',
        label: 'Credit',
        icon: Bookmark,
        description: 'Customer credit accounts and their balances.',
        perm: 'house_tab:read',
        feature: 'house_tabs',
      },
      {
        // finance:read is the balance gate (owner-only by default, matching the
        // API); transfer:read keeps the page reachable for a manager who still
        // records deposits but is not shown what the cafe holds. The page
        // renders only the half the member is entitled to.
        to: '/admin/accounts',
        label: 'Cafe balance',
        icon: Wallet,
        description: 'Cash drawer, bank and online balances, with transfers.',
        anyOf: ['finance:read', 'transfer:read'],
      },
      {
        to: '/admin/owners',
        label: 'Owners',
        icon: Crown,
        description: 'Ownership, equity, investments and loans.',
        perm: 'finance:read',
        feature: 'owner_finance',
      },
      {
        to: '/admin/insights',
        label: 'Findings',
        icon: ShieldCheck,
        description:
          'What the nightly check noticed in your books, and what you decided to do about it.',
        perm: 'insight:read',
      },
      {
        to: '/admin/reports',
        label: 'Reports',
        icon: BarChart3,
        description: 'Profitability by category and item-level sales movers.',
        perm: 'report:read',
      },
      {
        to: '/admin/people',
        label: 'People',
        icon: Users,
        description: 'Members, staff profiles and the roles that grant access.',
        anyOf: ['member:read', 'staff:read', 'role:read'],
      },
      {
        to: '/admin/activity',
        label: 'Activity',
        icon: History,
        description: 'Audit timeline of who changed what, when.',
        perm: 'audit:read',
        feature: 'audit_logs',
      },
      {
        to: '/admin/settings',
        label: 'Settings',
        icon: SettingsIcon,
        description: 'Branding, preferences, plan & usage, and integrations.',
        perm: 'tenant:update',
      },
    ],
  },
  {
    title: 'Learn',
    items: [
      {
        // One entry, five tabs (see LearnLayout). The guide, the walkthroughs,
        // the money sandbox and the site map used to be separate sidebar rows a
        // newcomer had to find one at a time.
        to: '/admin/learn',
        label: 'Learn & help',
        icon: GraduationCap,
        // No perm — learning material is open to every member of any tenant. The
        // calculations tab shows real figures, so it gates each one internally.
        description:
          'How every number is calculated, with your own figures — plus guides, walkthroughs, the money sandbox and the site map.',
      },
    ],
  },
];

function itemVisible(me: Me | undefined, item: NavItem): boolean {
  if (item.feature && !hasFeature(me, item.feature)) return false;
  if (item.anyOf) return canAny(me, ...item.anyOf);
  if (item.perm) return can(me, item.perm);
  return true;
}

/**
 * Filter the nav tree to the sections/items the member may see, dropping any
 * group left with no visible items. Used by both the sidebar and the Site map.
 */
export function visibleSections(me: Me | undefined): NavGroup[] {
  return NAV_SECTIONS.map((g) => ({
    ...g,
    items: g.items.filter((it) => itemVisible(me, it)),
  })).filter((g) => g.items.length > 0);
}
